import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime.js';
import SearchIcon from '@jetbrains/icons/search.js';
import RingButton from '@jetbrains/ring-ui/dist/button/button.js';
import Icon from '@jetbrains/ring-ui/dist/icon/icon.js';
import Popup from '@jetbrains/ring-ui/dist/popup/popup.js';
import Tooltip from '@jetbrains/ring-ui/dist/tooltip/tooltip.js';
import Checkbox from '@jetbrains/ring-ui/dist/checkbox/checkbox.js';
import cn from 'classnames';
import React__default, { useState, useMemo, useCallback } from 'react';
import FocusLock from 'react-focus-lock';
import { Button } from '../Button/Button.js';
import { TOOLTIP_DELAY } from '../../config/globalConstants.js';
import { getAttributeFromTaggedParent } from '../../utils/elementAttributes.js';
import { hasOwnProp } from '../../utils/object.js';
import { capitaliseStr, getShortStr } from '../../utils/string.js';
import { ReactComponent as SvgArrowDown } from '../../media/images/icons/12-8/arrowDown.svg.js';
import { ReactComponent as SvgClose } from '../../media/images/icons/12-8/close.svg.js';
import styles from './Select.module.css.js';

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var CHAR_LIM = 50;
var emptyCallback = function emptyCallback() {
  return undefined;
};
var Select = /*#__PURE__*/React__default.memo(function (props) {
  var _cn5, _cn7;
  // noinspection LocalVariableNamingConventionJS
  var _props$classNames = props.classNames,
    classes = _props$classNames === void 0 ? {} : _props$classNames,
    _props$defaultSelecti = props.defaultSelectionLabel,
    defaultSelectionLabel = _props$defaultSelecti === void 0 ? '' : _props$defaultSelecti,
    directions = props.directions,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$htmlProps = props.htmlProps,
    htmlProps = _props$htmlProps === void 0 ? {} : _props$htmlProps,
    Icon$1 = props.Icon,
    _props$noChevron = props.noChevron,
    noChevron = _props$noChevron === void 0 ? false : _props$noChevron,
    _props$onClose = props.onClose,
    onClose = _props$onClose === void 0 ? null : _props$onClose,
    _props$onDeleteSelect = props.onDeleteSelection,
    onDeleteSelection = _props$onDeleteSelect === void 0 ? null : _props$onDeleteSelect,
    _props$onDeselect = props.onDeselect,
    onDeselect = _props$onDeselect === void 0 ? null : _props$onDeselect,
    _props$onOpen = props.onOpen,
    onOpen = _props$onOpen === void 0 ? null : _props$onOpen,
    _props$onSelect = props.onSelect,
    onSelect = _props$onSelect === void 0 ? null : _props$onSelect,
    options = props.options,
    _props$search = props.search,
    search = _props$search === void 0 ? false : _props$search,
    selected = props.selected,
    _props$selectName = props.selectName,
    selectName = _props$selectName === void 0 ? '' : _props$selectName,
    _props$showSelectedTo = props.showSelectedTooltip,
    showSelectedTooltip = _props$showSelectedTo === void 0 ? true : _props$showSelectedTo,
    specialButton = props.specialButton,
    _props$showDisabledCh = props.showDisabledChevron,
    showDisabledChevron = _props$showDisabledCh === void 0 ? false : _props$showDisabledCh;
  var classNames = _objectSpread({
    container: '',
    chevron: '',
    icon: '',
    itemContainer: '',
    label: '',
    list: '',
    popup: '',
    labelOpened: '',
    specialButton: '',
    placeholder: ''
  }, classes);
  var htmlPropsChecked = _objectSpread({
    closeBtn: {},
    list: {},
    search: {},
    specialBtn: {},
    triggerBtn: {}
  }, htmlProps);
  var multiple = Array.isArray(selected);
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    searchQuery = _useState2[0],
    setSearchQuery = _useState2[1];
  var _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    open = _useState4[0],
    setOpen = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    showFade = _useState6[0],
    setShowFade = _useState6[1];
  var showTooltip = !open && !disabled && showSelectedTooltip;
  var label = useMemo(function () {
    var selectedActiveNames = selected !== undefined ? options.filter(function (el) {
      return (multiple ? selected.includes(el.key) : selected === el.key) && !el.disabled;
    }).map(function (el) {
      return el.label;
    }) : [];
    if (!multiple) {
      selectedActiveNames = selectedActiveNames.slice(0, 1);
    }
    return createLabel(selectedActiveNames, selectName, CHAR_LIM);
  }, [multiple, options, selectName, selected]);
  var selectValue = label || defaultSelectionLabel;
  var labelPrefix = useMemo(function () {
    return selectName ? capitaliseStr(selectName) : '';
  }, [selectName]);
  var activeSelected = useMemo(function () {
    if (selected !== undefined) {
      return options.filter(function (el) {
        return !el.disabled && (multiple ? selected.includes(el.key) : selected === el.key);
      }).map(function (el) {
        return el.label;
      });
    }
    return [];
  }, [options, selected, multiple]);
  var activeImage = useMemo(function () {
    if (selected !== undefined) {
      var _selectedItem;
      var selectedItem;
      if (multiple && selected.length === 1) {
        selectedItem = options.find(function (el) {
          return el.key === selected[0];
        });
      } else if (!multiple) {
        selectedItem = options.find(function (el) {
          return el.key === selected;
        });
      }
      return (_selectedItem = selectedItem) === null || _selectedItem === void 0 ? void 0 : _selectedItem.image;
    }
    return undefined;
  }, [options, selected, multiple]);
  var handleChangeSearch = useCallback(function (e) {
    setSearchQuery(e.target.value);
    if (showFade) {
      setShowFade(false);
    }
  }, [showFade]);
  var handleCloseAttempt = useCallback(function () {
    setOpen(false);
    setSearchQuery('');
    if (onClose) {
      onClose();
    }
  }, [onClose]);
  var handleTriggerClick = useCallback(function () {
    if (!open) {
      setOpen(true);
      if (onOpen) {
        onOpen();
      }
    } else {
      handleCloseAttempt();
    }
  }, [open, onOpen, handleCloseAttempt]);
  var handleKeyDown = useCallback(function (e) {
    if (e.key === 'Escape') {
      handleCloseAttempt();
    }
  }, [handleCloseAttempt]);
  var handleScroll = useCallback(function (e) {
    var hitBottom = e.currentTarget.scrollHeight - e.currentTarget.scrollTop === e.currentTarget.clientHeight;
    if (hitBottom && showFade) {
      setShowFade(false);
    } else if (!hitBottom && !showFade) {
      setShowFade(true);
    }
  }, [showFade]);
  var toggleOption = useCallback(function (e) {
    var key = getAttributeFromTaggedParent(e.currentTarget, 'LI', 'data-qd-key');
    var checked = e.currentTarget.getAttribute('data-qd-checked') === 'true' || getAttributeFromTaggedParent(e.currentTarget, 'SPAN', 'data-qd-checked') === 'true';
    if (!checked && onSelect) {
      onSelect(key, selectName);
    }
    if (checked && onDeselect) {
      onDeselect(key, selectName);
    }
  }, [onDeselect, onSelect, selectName]);
  var handleOptionClick = useCallback(function (e) {
    toggleOption(e);
  }, [toggleOption]);
  var handleOptionKeyDown = useCallback(function (e) {
    if (['Enter', ' ', 'Spacebar', 'Space'].includes(e.key)) {
      toggleOption(e);
    }
  }, [toggleOption]);
  var handleSelectSingle = useCallback(function (e) {
    var key = getAttributeFromTaggedParent(e.currentTarget, 'LI', 'data-qd-key');
    if (onSelect) {
      onSelect(key, selectName);
      setOpen(false);
      if (onClose) {
        onClose();
      }
    }
  }, [onSelect, onClose, selectName]);
  var handleDeleteSelection = useCallback(function (e) {
    e.preventDefault();
    if (onDeleteSelection) {
      onDeleteSelection(selectName);
    }
  }, [selectName, onDeleteSelection]);
  var handleSpecialClick = function handleSpecialClick() {
    if (specialButton) {
      specialButton.onClick();
      setOpen(false);
    }
  };
  var searchedOptions = options.filter(function (obj) {
    return obj.label.toLowerCase().includes(searchQuery.toLowerCase());
  });
  var renderSearchedOption = function renderSearchedOption(obj) {
    var key = obj.key,
      label = obj.label,
      disabled = obj.disabled,
      template = obj.template,
      image = obj.image,
      description = obj.description;
    var checked = selected !== undefined && selected.includes(key);
    return jsx("li", {
      className: styles.listItem,
      "data-qd-key": key,
      children: multiple ?
      // Not a label because we need to prevent two events firing (on input and label), which messes up clicks tracking
      jsxs("span", {
        "aria-checked": checked,
        "aria-disabled": disabled,
        "aria-label": label,
        className: cn(styles.itemLabel, classNames.itemContainer, _defineProperty({}, styles.disabled, disabled)),
        "data-qd-checked": checked,
        onClick: disabled ? emptyCallback : handleOptionClick,
        onKeyDown: disabled ? emptyCallback : handleOptionKeyDown,
        role: 'checkbox',
        tabIndex: 0,
        title: label,
        children: [jsx(Checkbox // fully controlled
        , {
          checked: checked,
          containerClassName: styles.itemCheckbox,
          disabled: disabled,
          onChange: emptyCallback,
          tabIndex: -1,
          title: label
        }), image && jsx("div", {
          className: styles.itemIcon,
          children: image
        }), jsx("span", {
          className: template ? styles.itemTemplateContainer : styles.itemName,
          children: template || label
        }), hasOwnProp(obj, 'description') && jsx("span", {
          className: styles.itemDesc,
          children: description
        })]
      }) : jsx(RingButton, {
        className: cn(styles.itemBtn, _defineProperty({}, styles.disabled, disabled)),
        disabled: disabled,
        onClick: handleSelectSingle,
        text: true,
        title: label,
        children: jsxs("span", {
          className: cn(styles.itemLabel, classNames.itemContainer, _defineProperty({}, styles.disabled, disabled)),
          children: [image && jsx("div", {
            className: styles.itemIcon,
            children: image
          }), jsx("span", {
            className: template ? styles.itemTemplateContainer : styles.itemName,
            children: template || label
          }), description && jsx("span", {
            className: styles.itemDesc,
            children: description
          })]
        })
      })
    }, key);
  };
  return (
    // TODO: Improve a11y. Which role will be better using there?
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    jsxs("div", {
      className: cn(styles.container, classNames.container),
      onKeyDown: handleKeyDown,
      children: [jsxs(Tooltip, {
        delay: TOOLTIP_DELAY,
        popupProps: {
          className: cn(styles.tooltipPopup, _defineProperty({}, styles.tooltipHidden, !showTooltip))
        },
        title: showTooltip && !!activeSelected.length ? activeSelected.join(', ') : '',
        children: [jsx(RingButton, _objectSpread(_objectSpread({
          className: cn(styles.label, classNames.label, (_cn5 = {}, _defineProperty(_cn5, styles.active, open), _defineProperty(_cn5, classNames.labelOpened, open), _defineProperty(_cn5, styles.disabled, disabled), _cn5)),
          disabled: disabled,
          onClick: handleTriggerClick,
          text: true
        }, htmlPropsChecked.triggerBtn), {}, {
          children: jsxs("span", {
            className: cn(styles.labelContainer, classNames.labelContainer),
            children: [!!Icon$1 && jsx(Icon$1, {
              className: cn(styles.mainIcon, _defineProperty({}, styles.disabled, disabled), classNames.icon)
            }), labelPrefix && jsxs("span", {
              children: [labelPrefix, selectName && selectValue && jsx(Fragment, {
                children: ":\xA0"
              })]
            }), activeImage && jsx("span", {
              className: cn(styles.itemIcon, (_cn7 = {}, _defineProperty(_cn7, styles.itemIconMarginLeft, selectName), _defineProperty(_cn7, styles.itemIconDisabled, disabled), _cn7), classNames.icon),
              children: activeImage
            }), selectValue && jsx("span", {
              className: cn(_defineProperty({}, classNames.placeholder, defaultSelectionLabel && !label)),
              children: selectValue
            }), (!disabled || showDisabledChevron) && !noChevron && jsx("div", {
              className: cn(styles.icon, classNames.chevronIcon),
              children: jsx(SvgArrowDown, {
                className: cn(styles.label, styles.chevronDown, classNames.chevron)
              })
            })]
          })
        })), jsx(Popup, _objectSpread(_objectSpread({
          className: cn(styles.popup, classNames.popup),
          directions: directions,
          hidden: !open,
          onCloseAttempt: handleCloseAttempt
        }, htmlPropsChecked.search), {}, {
          children: jsxs(FocusLock, {
            returnFocus: true,
            children: [search && jsxs("div", {
              className: styles.searchWrapper,
              children: [jsx(Icon, {
                glyph: SearchIcon,
                className: styles.searchIcon
              }), jsx("input", _objectSpread({
                autoComplete: 'off',
                placeholder: typeof search === 'string' ? search : "Search ".concat(selectName),
                onChange: handleChangeSearch
              }, htmlPropsChecked.search))]
            }), !searchedOptions.length && jsx("p", {
              className: styles.empty,
              children: "No options found"
            }), jsx("ul", _objectSpread(_objectSpread({
              className: cn(styles.list, _defineProperty({}, styles.morePadding, !!specialButton), classNames.list),
              onScroll: handleScroll
            }, htmlPropsChecked.list), {}, {
              children: searchedOptions.map(renderSearchedOption)
            })), jsxs("div", {
              className: styles.bottom,
              children: [jsx("div", {
                className: "".concat(styles.fade, " ").concat(showFade ? styles.fadeActive : '')
              }), !!specialButton && jsx(Button, _objectSpread(_objectSpread({
                variant: "clear",
                className: cn(styles.specialButton, classNames.specialButton),
                onClick: handleSpecialClick,
                disabled: specialButton.disabled,
                title: specialButton.label
              }, htmlPropsChecked.specialBtn), {}, {
                children: specialButton.label
              }))]
            })]
          })
        }))]
      }), selected !== undefined && !!selected.length && !disabled && !!onDeleteSelection && jsx("button", _objectSpread(_objectSpread({
        className: styles.icon,
        onClick: handleDeleteSelection
      }, htmlPropsChecked.closeBtn), {}, {
        children: jsx(SvgClose, {})
      }))]
    })
  );
});
// using typecasting because generic props lost with React memo
// https://github.com/DefinitelyTyped/DefinitelyTyped/issues/37087
function createLabel(options, name, charLim) {
  var selectLabel = '';
  if (options.length > 1) {
    selectLabel = "".concat(options.length, " selected");
  }
  if (options.length === 1) {
    var full = "".concat(options.join(', '));
    selectLabel = getShortStr(full, charLim);
  }
  return selectLabel;
}

export { Select };
